import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  PlayCircle, FileText, HelpCircle, ChevronLeft, ChevronRight,
  CheckCircle, Menu, X, ArrowLeft, Loader2, Volume2, VolumeX,
  MessageCircle, Send, Pause, Play, Download
} from 'lucide-react';
import ShareButtons from '../components/ShareButtons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoursePlayer = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [course, setCourse] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [progress, setProgress] = useState({ completed_lessons: [], progress_percent: 0 });
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [lessonQuiz, setLessonQuiz] = useState(null);
  const [loadingQuiz, setLoadingQuiz] = useState(false);

  // Audio state
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // AI Coach state
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachMessages, setCoachMessages] = useState([]);
  const [coachInput, setCoachInput] = useState('');
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachSessionId, setCoachSessionId] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    fetchCourseAndProgress();
  }, [courseId]);

  const fetchCourseAndProgress = async () => {
    try {
      const [courseRes, progressRes] = await Promise.all([
        axios.get(`${API}/courses/${courseId}`),
        axios.get(`${API}/courses/${courseId}/progress`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCourse(courseRes.data);
      setProgress(progressRes.data);
      
      const lessons = courseRes.data.lessons?.sort((a, b) => a.order - b.order) || [];
      if (lessons.length > 0) {
        // Find first incomplete lesson or start from beginning
        const firstIncomplete = lessons.find(l => !progressRes.data.completed_lessons?.includes(l.id));
        const startLesson = firstIncomplete || lessons[0];
        setCurrentLesson(startLesson);
        fetchLessonQuiz(startLesson.id);
      }
    } catch (error) {
      toast.error('Failed to load course');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const markComplete = async (lessonId, completed = true) => {
    try {
      const response = await axios.post(`${API}/courses/${courseId}/progress`, {
        lesson_id: lessonId,
        completed
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgress(response.data);
      if (completed) {
        toast.success('Lesson marked as complete!');
      }
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const selectLesson = (lesson) => {
    setCurrentLesson(lesson);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setLessonQuiz(null);
    fetchLessonQuiz(lesson.id);
    // Reset audio state
    if (audioRef.current) { audioRef.current.pause(); }
    setAudioUrl(null);
    setIsPlaying(false);
    // Scroll content to top
    window.scrollTo(0, 0);
    const contentEl = document.querySelector('[data-testid="lesson-content-area"]');
    if (contentEl) contentEl.scrollTop = 0;
  };

  // ── Audio Functions ──
  const fetchAudio = async () => {
    if (!currentLesson || audioLoading) return;
    setAudioLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/courses/${courseId}/lessons/${currentLesson.id}/audio`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.audio_url) {
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.audio_url}`;
        setAudioUrl(fullUrl);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to generate audio';
      toast.error(msg);
    } finally {
      setAudioLoading(false);
    }
  };

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  // ── AI Coach Functions ──
  const sendCoachMessage = async () => {
    const msg = coachInput.trim();
    if (!msg || coachLoading) return;
    setCoachInput('');
    setCoachMessages(prev => [...prev, { role: 'user', content: msg }]);
    setCoachLoading(true);
    try {
      const { data } = await axios.post(`${API}/academy/coach/chat`, {
        message: msg,
        session_id: coachSessionId,
        lesson_id: currentLesson?.id || null,
        course_slug: course?.slug || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCoachSessionId(data.session_id);
      setCoachMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Coach error';
      setCoachMessages(prev => [...prev, { role: 'assistant', content: `Error: ${detail}` }]);
    } finally {
      setCoachLoading(false);
    }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [coachMessages]);

  const fetchLessonQuiz = async (lessonId) => {
    setLoadingQuiz(true);
    try {
      const { data } = await axios.get(`${API}/courses/${courseId}/lessons/${lessonId}/quiz`);
      if (data.has_quiz) {
        setLessonQuiz(data);
      }
    } catch {
      // No quiz or error - that's fine
    } finally {
      setLoadingQuiz(false);
    }
  };

  const handleLessonQuizSubmit = async () => {
    if (!lessonQuiz) return;
    try {
      const { data } = await axios.post(
        `${API}/courses/${courseId}/lessons/${currentLesson.id}/quiz/submit`,
        { answers: quizAnswers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuizSubmitted(true);
      if (data.passed) {
        toast.success(`Quiz passed! Score: ${data.score}% (${data.correct}/${data.total})`);
      } else {
        toast.error(`Score: ${data.score}% — Need 70% to pass. Try again!`);
      }
    } catch {
      toast.error('Failed to submit quiz');
    }
  };

  const sortedLessons = (course?.lessons || []).slice().sort((a, b) => a.order - b.order);

  const goToNextLesson = () => {
    const currentIndex = sortedLessons.findIndex(l => l.id === currentLesson.id);
    if (currentIndex < sortedLessons.length - 1) {
      selectLesson(sortedLessons[currentIndex + 1]);
    }
  };

  const goToPrevLesson = () => {
    const currentIndex = sortedLessons.findIndex(l => l.id === currentLesson.id);
    if (currentIndex > 0) {
      selectLesson(sortedLessons[currentIndex - 1]);
    }
  };

  const handleQuizSubmit = () => {
    setQuizSubmitted(true);
    // In a real app, you'd validate answers here
    toast.success('Quiz submitted!');
    markComplete(currentLesson.id);
  };

  const getLessonIcon = (type, isComplete) => {
    if (isComplete) return <CheckCircle className="w-4 h-4 text-green-500" />;
    switch (type) {
      case 'video': return <PlayCircle className="w-4 h-4" />;
      case 'text': return <FileText className="w-4 h-4" />;
      case 'quiz': return <HelpCircle className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (!course || !currentLesson) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">
        <p>No content available</p>
      </div>
    );
  }

  const currentIndex = sortedLessons.findIndex(l => l.id === currentLesson.id);
  const isComplete = progress.completed_lessons?.includes(currentLesson.id);

  const renderLessonQuiz = () => {
    if (!lessonQuiz || !lessonQuiz.has_quiz) return null;
    return (
      <div className="mt-8 pt-6 border-t border-slate-200" data-testid="lesson-quiz-section">
        <h3 className="font-heading text-lg font-semibold mb-4 not-prose flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-indigo-500" /> Lesson Quiz
        </h3>
        <div className="space-y-4 not-prose">
          {lessonQuiz.questions.map((q, qIndex) => (
            <div key={qIndex} className="space-y-2">
              <p className="font-medium text-slate-800">{qIndex + 1}. {q.question}</p>
              <div className="space-y-1.5 pl-4">
                {q.options.map((option, oIndex) => (
                  <label
                    key={oIndex}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors text-sm ${
                      quizSubmitted
                        ? oIndex === q.correct_index
                          ? 'bg-green-50 border-green-300'
                          : quizAnswers[String(qIndex)] === oIndex
                            ? 'bg-red-50 border-red-300'
                            : 'border-slate-200'
                        : quizAnswers[String(qIndex)] === oIndex
                          ? 'bg-indigo-50 border-indigo-300'
                          : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`lq-${qIndex}`}
                      checked={quizAnswers[String(qIndex)] === oIndex}
                      onChange={() => setQuizAnswers({ ...quizAnswers, [String(qIndex)]: oIndex })}
                      disabled={quizSubmitted}
                      className="sr-only"
                    />
                    <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      quizAnswers[String(qIndex)] === oIndex ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'
                    }`}>
                      {quizAnswers[String(qIndex)] === oIndex && <span className="w-2 h-2 rounded-full bg-white"></span>}
                    </span>
                    <span className="text-slate-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          {!quizSubmitted && lessonQuiz.questions.length > 0 && (
            <Button onClick={handleLessonQuizSubmit} className="mt-3" data-testid="submit-lesson-quiz-btn">
              Submit Quiz
            </Button>
          )}
          {quizSubmitted && (
            <Button variant="outline" onClick={() => { setQuizSubmitted(false); setQuizAnswers({}); }} className="mt-3">
              Retry Quiz
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (currentLesson.content_type) {
      case 'video':
        return (
          <div className="aspect-video bg-black rounded-lg overflow-hidden">
            {currentLesson.content.includes('youtube.com') || currentLesson.content.includes('youtu.be') ? (
              <iframe
                src={currentLesson.content.replace('watch?v=', 'embed/')}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video 
                src={currentLesson.content} 
                controls 
                className="w-full h-full"
              />
            )}
          </div>
        );
      
      case 'text':
      case 'markdown':
        return (
          <div className="bg-white rounded-lg p-8 prose prose-slate max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {currentLesson.content || ''}
            </ReactMarkdown>
            {currentLesson.short_summary && (
              <div className="mt-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200" data-testid="lesson-takeaway">
                <h4 className="text-indigo-800 font-semibold text-sm mb-1">Key Takeaway</h4>
                <p className="text-indigo-700 text-sm m-0">{currentLesson.short_summary}</p>
              </div>
            )}
            {renderLessonQuiz()}
          </div>
        );
      
      case 'quiz':
        let quizData;
        try {
          quizData = JSON.parse(currentLesson.content);
        } catch {
          quizData = { questions: [] };
        }
        
        return (
          <div className="bg-white rounded-lg p-8 space-y-6">
            <h3 className="font-heading text-xl font-semibold">Quiz</h3>
            {quizData.questions?.map((q, qIndex) => (
              <div key={qIndex} className="space-y-3">
                <p className="font-medium">{qIndex + 1}. {q.question}</p>
                <div className="space-y-2 pl-4">
                  {q.options?.map((option, oIndex) => (
                    <label 
                      key={oIndex}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        quizSubmitted 
                          ? option === q.correct 
                            ? 'bg-green-50 border-green-300' 
                            : quizAnswers[qIndex] === option 
                              ? 'bg-red-50 border-red-300'
                              : 'border-slate-200'
                          : quizAnswers[qIndex] === option
                            ? 'bg-primary/5 border-primary'
                            : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`question-${qIndex}`}
                        value={option}
                        checked={quizAnswers[qIndex] === option}
                        onChange={() => setQuizAnswers({ ...quizAnswers, [qIndex]: option })}
                        disabled={quizSubmitted}
                        className="sr-only"
                      />
                      <span className="text-slate-700">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            {!quizSubmitted && quizData.questions?.length > 0 && (
              <Button onClick={handleQuizSubmit} className="mt-4" data-testid="submit-quiz-btn">
                Submit Quiz
              </Button>
            )}
          </div>
        );
      
      default:
        return <p className="text-slate-400">Unknown content type</p>;
    }
  };

  return (
    <div className="h-screen bg-slate-900 flex overflow-hidden" data-testid="course-player">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-80' : 'w-0'} flex-shrink-0 bg-slate-800 border-r border-slate-700 transition-all duration-300 overflow-hidden`}>
        <div className="w-80 h-screen flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-slate-700">
            <Button 
              variant="ghost" 
              size="sm"
              className="text-slate-400 hover:text-white mb-4"
              onClick={() => navigate(`/courses/${courseId}`)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Course
            </Button>
            <h2 className="font-heading font-semibold text-white truncate">{course.title}</h2>
            <div className="mt-3">
              <div className="flex items-center justify-between text-sm text-slate-400 mb-2">
                <span>{Math.round(progress.progress_percent)}% complete</span>
                <span>{progress.completed_lessons?.length || 0}/{sortedLessons.length}</span>
              </div>
              <Progress value={progress.progress_percent} className="h-2" />
            </div>
          </div>

          {/* Lessons List */}
          <ScrollArea className="flex-1">
            <div className="p-2">
              {sortedLessons.map((lesson, index) => {
                const isLessonComplete = progress.completed_lessons?.includes(lesson.id);
                const isCurrent = lesson.id === currentLesson.id;
                
                return (
                  <button
                    key={lesson.id}
                    onClick={() => selectLesson(lesson)}
                    className={`w-full text-left p-3 rounded-lg mb-1 transition-colors ${
                      isCurrent 
                        ? 'bg-primary text-white' 
                        : 'text-slate-300 hover:bg-slate-700'
                    }`}
                    data-testid={`lesson-nav-${lesson.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                        isCurrent ? 'bg-white/20' : 'bg-slate-700'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-sm">{lesson.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {getLessonIcon(lesson.content_type, isLessonComplete)}
                          <span className="text-xs opacity-70 capitalize">{lesson.content_type}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <div className="h-14 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-slate-400 hover:text-white"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <div>
              <p className="text-white font-medium">{currentLesson.title}</p>
              <p className="text-sm text-slate-400">Lesson {currentIndex + 1} of {sortedLessons.length}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Audio Button */}
            {!audioUrl ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchAudio}
                disabled={audioLoading}
                className="text-slate-300 hover:text-white"
                data-testid="lesson-audio-btn"
              >
                {audioLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Volume2 className="w-4 h-4 mr-1" />}
                {audioLoading ? 'Generating...' : 'Listen'}
              </Button>
            ) : (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={toggleAudio} className="text-slate-300 hover:text-white h-8 w-8" data-testid="audio-play-toggle">
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </Button>
                <audio ref={audioRef} src={audioUrl} onEnded={() => setIsPlaying(false)} />
              </div>
            )}
            {/* AI Coach Button */}
            <Button
              variant={coachOpen ? "default" : "ghost"}
              size="sm"
              onClick={() => setCoachOpen(!coachOpen)}
              className={coachOpen ? "bg-indigo-600 hover:bg-indigo-700 text-white" : "text-slate-300 hover:text-white"}
              data-testid="ai-coach-toggle-btn"
            >
              <MessageCircle className="w-4 h-4 mr-1" />
              AI Coach
            </Button>
            <Checkbox
              id="mark-complete"
              checked={isComplete}
              onCheckedChange={(checked) => markComplete(currentLesson.id, checked)}
              data-testid="mark-complete-checkbox"
            />
            <label htmlFor="mark-complete" className="text-sm text-slate-300 cursor-pointer">
              Complete
            </label>
            <ShareButtons title={course.title} description={course.description} />
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 flex min-h-0">
          <div className={`${coachOpen ? 'w-3/5' : 'w-full'} transition-all duration-300 overflow-auto p-6`}>
            <div className="max-w-4xl mx-auto" data-testid="lesson-content-area">
              {renderContent()}
            </div>
          </div>

          {/* AI Coach Panel */}
          {coachOpen && (
            <div className="w-2/5 min-w-[340px] border-l border-slate-700 bg-slate-800 flex flex-col" style={{overflow: 'hidden', maxWidth: '40%'}} data-testid="ai-coach-panel">
              <div className="p-3 border-b border-slate-700 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-indigo-400" />
                  <span className="text-white font-medium text-sm">AI Coach</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setCoachOpen(false)} className="text-slate-400 hover:text-white h-7 w-7">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <div className="p-4" style={{maxWidth: '100%', boxSizing: 'border-box'}}>
                {coachMessages.length === 0 && (
                  <div className="text-center text-slate-400 mt-8 px-2">
                    <MessageCircle className="w-8 h-8 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Ask me anything about this lesson!</p>
                    <div className="mt-4 space-y-2">
                      {['Explain this lesson simply', 'Give me a real-world example', 'What should I do next?'].map((q) => (
                        <button
                          key={q}
                          onClick={() => { setCoachInput(q); }}
                          className="block w-full text-left text-xs p-2 rounded bg-slate-700/50 hover:bg-slate-700 text-slate-300 transition-colors"
                          data-testid={`coach-suggestion-${q.slice(0,10)}`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {coachMessages.map((msg, i) => (
                  <div key={i} className={`mb-3 ${msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'}`} style={{maxWidth: '100%'}}>
                    <div className={`p-3 rounded-lg text-sm ${
                      msg.role === 'user'
                        ? 'bg-indigo-600 text-white max-w-[85%]'
                        : 'bg-slate-700 text-slate-200'
                    }`} style={{overflowWrap: 'anywhere', wordBreak: 'break-word', overflow: 'hidden', maxWidth: msg.role === 'user' ? '85%' : '100%', minWidth: 0}}>
                      {msg.role === 'assistant' ? (
                        <div className="prose prose-sm prose-invert [&_pre]:overflow-x-auto [&_pre]:max-w-full [&_table]:block [&_table]:overflow-x-auto [&_table]:max-w-full [&_code]:break-all [&_ol]:pl-4 [&_ul]:pl-4 [&_img]:max-w-full" style={{overflowWrap: 'anywhere', wordBreak: 'break-word', overflow: 'hidden', maxWidth: '100%'}}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                        </div>
                      ) : msg.content}
                    </div>
                  </div>
                ))}
                {coachLoading && (
                  <div className="flex items-center gap-2 text-slate-400 text-sm mb-3">
                    <Loader2 className="w-3 h-3 animate-spin" /> Thinking...
                  </div>
                )}
                <div ref={chatEndRef} />
                </div>
              </div>
              <div className="p-3 border-t border-slate-700 shrink-0">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={coachInput}
                    onChange={(e) => setCoachInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendCoachMessage()}
                    placeholder="Ask the AI Coach..."
                    className="flex-1 bg-slate-700 border-0 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    data-testid="coach-input"
                  />
                  <Button
                    size="icon"
                    onClick={sendCoachMessage}
                    disabled={!coachInput.trim() || coachLoading}
                    className="bg-indigo-600 hover:bg-indigo-700 h-9 w-9"
                    data-testid="coach-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="h-16 bg-slate-800 border-t border-slate-700 flex items-center justify-between px-6">
          <Button
            variant="outline"
            onClick={goToPrevLesson}
            disabled={currentIndex === 0}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            data-testid="prev-lesson-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          <Button
            onClick={goToNextLesson}
            disabled={currentIndex === sortedLessons.length - 1}
            className="bg-primary hover:bg-primary/90"
            data-testid="next-lesson-btn"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CoursePlayer;
